import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  cn,
  Badge,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@pantry/ui';
import {
  useAdminProductDetail,
  useAdminUpdateProduct,
  useAdminDeleteProduct,
  useCurrentUser,
  type AdminProduct,
  type AdminUpdateProductData,
  PRODUCT_CATEGORIES,
} from '@pantry/api-client';
import { hasAppPermission } from '@pantry/shared';
import {
  XMarkIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PencilIcon,
  GlobeAltIcon,
  CheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

function getSourceLabels(t: ReturnType<typeof useLingui>['t']): Record<string, string> {
  return {
    openfoodfacts: t`Open Food Facts`,
    user: t`User Contributed`,
    manual: t`Manual Entry`,
  };
}

const SOURCE_COLORS: Record<string, string> = {
  openfoodfacts: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  manual: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

interface ProductDetailsSheetProps {
  product: AdminProduct;
  onClose: () => void;
}

export function ProductDetailsSheet({ product, onClose }: ProductDetailsSheetProps) {
  const { t, i18n } = useLingui();
  const SOURCE_LABELS = getSourceLabels(t);
  const { data: currentUser } = useCurrentUser();
  const { data: productDetail, isLoading } = useAdminProductDetail(product.id);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Check permissions
  const canEdit = currentUser && hasAppPermission(currentUser.appRole, 'app:products:edit');
  const canDelete = currentUser && hasAppPermission(currentUser.appRole, 'app:products:delete');

  const isOffProduct = product.source === 'openfoodfacts';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
        <div
          className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-xl animate-in slide-in-from-right duration-200 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold">
              <Trans>Product Details</Trans>
            </h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-lg">
              <XMarkIcon className="h-5 w-5" />
              <span className="sr-only">
                <Trans>Close</Trans>
              </span>
            </Button>
          </div>

          <div className="p-6">
            {isEditing ? (
              <EditProductForm
                product={productDetail || product}
                onCancel={() => setIsEditing(false)}
                onSuccess={() => setIsEditing(false)}
              />
            ) : (
              <>
                {/* Product header */}
                <div className="flex items-start gap-4 mb-6">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="h-20 w-20 rounded-xl object-cover bg-muted"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-muted">
                      <CubeIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{product.name}</p>
                    {product.brand && (
                      <p className="text-sm text-muted-foreground">{product.brand}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge
                        variant="secondary"
                        className={cn('font-medium', SOURCE_COLORS[product.source])}
                      >
                        {SOURCE_LABELS[product.source]}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* OFF Source Warning */}
                {isOffProduct && canEdit && (
                  <div className="mb-6 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-amber-800 dark:text-amber-200">
                    <GlobeAltIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      <Trans>
                        This product is from Open Food Facts. Editing will override the original
                        data.
                      </Trans>
                    </p>
                  </div>
                )}

                {/* Product Info */}
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    <Trans>Product Info</Trans>
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">
                        <Trans>Barcode</Trans>
                      </span>
                      <span>
                        {product.barcode || (
                          <span className="text-amber-500">
                            <Trans>None</Trans>
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">
                        <Trans>Category</Trans>
                      </span>
                      <span>
                        {product.category || (
                          <span className="text-amber-500">
                            <Trans>None</Trans>
                          </span>
                        )}
                      </span>
                    </div>
                    {productDetail?.description && (
                      <div className="py-2 border-b border-border/50">
                        <span className="text-muted-foreground block mb-1">
                          <Trans>Description</Trans>
                        </span>
                        <span className="text-foreground">{productDetail.description}</span>
                      </div>
                    )}
                    {productDetail?.defaultShelfLifeDays && (
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">
                          <Trans>Default Shelf Life</Trans>
                        </span>
                        <span>
                          <Trans>{productDetail.defaultShelfLifeDays} days</Trans>
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-border/50">
                      <span className="text-muted-foreground">
                        <Trans>Created</Trans>
                      </span>
                      <span>{new Date(product.createdAt).toLocaleDateString(i18n.locale)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">
                        <Trans>Last Updated</Trans>
                      </span>
                      <span>{new Date(product.updatedAt).toLocaleDateString(i18n.locale)}</span>
                    </div>
                  </div>
                </section>

                {/* Scores */}
                {(productDetail?.ecoscoreGrade ||
                  productDetail?.nutriscoreGrade ||
                  productDetail?.novaGroup) && (
                  <section className="mb-6">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">
                      <Trans context="section_header">Scores</Trans>
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {productDetail?.nutriscoreGrade && (
                        <div className="rounded-lg bg-muted/50 p-3 text-center">
                          <p className="text-lg font-semibold uppercase">
                            {productDetail.nutriscoreGrade}
                          </p>
                          <p className="text-xs text-muted-foreground">Nutri-Score</p>
                        </div>
                      )}
                      {productDetail?.ecoscoreGrade && (
                        <div className="rounded-lg bg-muted/50 p-3 text-center">
                          <p className="text-lg font-semibold uppercase">
                            {productDetail.ecoscoreGrade}
                          </p>
                          <p className="text-xs text-muted-foreground">Eco-Score</p>
                        </div>
                      )}
                      {productDetail?.novaGroup && (
                        <div className="rounded-lg bg-muted/50 p-3 text-center">
                          <p className="text-lg font-semibold">{productDetail.novaGroup}</p>
                          <p className="text-xs text-muted-foreground">NOVA</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Usage Stats */}
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    <Trans>Usage</Trans>
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg bg-muted/50 p-3 text-center">
                      <p className="text-2xl font-semibold">{product.itemCount}</p>
                      <p className="text-xs text-muted-foreground">
                        <Trans>Items</Trans>
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-3 text-center">
                      <p className="text-2xl font-semibold">{productDetail?.householdCount || 0}</p>
                      <p className="text-xs text-muted-foreground">
                        <Trans>Households</Trans>
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-3 text-center">
                      <p className="text-2xl font-semibold">
                        {productDetail?.activeInstanceCount || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <Trans>Active</Trans>
                      </p>
                    </div>
                  </div>
                </section>

                {/* Actions */}
                <div className="flex gap-3">
                  {canEdit && (
                    <Button variant="outline" className="flex-1" onClick={() => setIsEditing(true)}>
                      <PencilIcon className="h-4 w-4 mr-2" />
                      <Trans>Edit</Trans>
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="outline"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <DeleteProductDialog
          product={product}
          onClose={() => setShowDeleteDialog(false)}
          onDeleted={onClose}
        />
      )}
    </>
  );
}

interface EditProductFormProps {
  product: AdminProduct;
  onCancel: () => void;
  onSuccess: () => void;
}

function EditProductForm({ product, onCancel, onSuccess }: EditProductFormProps) {
  const { t } = useLingui();

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'fruits_vegetables':
        return t`Fruits & Vegetables`;
      case 'dairy_products':
        return t`Dairy Products`;
      case 'meat_poultry':
        return t`Meat & Poultry`;
      case 'seafood':
        return t`Seafood`;
      case 'bakery':
        return t`Bakery`;
      case 'beverages':
        return t`Beverages`;
      case 'snacks':
        return t`Snacks`;
      case 'frozen_foods':
        return t`Frozen Foods`;
      case 'canned_goods':
        return t`Canned Goods`;
      case 'condiments_sauces':
        return t`Condiments & Sauces`;
      case 'grains_pasta':
        return t`Grains & Pasta`;
      case 'breakfast_cereals':
        return t`Breakfast & Cereals`;
      case 'household_items':
        return t`Household Items`;
      case 'personal_care':
        return t`Personal Care`;
      case 'baby_products':
        return t`Baby Products`;
      case 'pet_supplies':
        return t`Pet Supplies`;
      case 'other':
        return t`Other`;
      default:
        return category.replace(/_/g, ' ');
    }
  };

  const [formData, setFormData] = useState<AdminUpdateProductData>({
    name: product.name,
    barcode: product.barcode,
    brand: product.brand,
    category: product.category as AdminUpdateProductData['category'],
  });

  const updateProduct = useAdminUpdateProduct();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only send changed fields
    const changes: AdminUpdateProductData = {};
    if (formData.name !== product.name) changes.name = formData.name;
    if (formData.barcode !== product.barcode) changes.barcode = formData.barcode;
    if (formData.brand !== product.brand) changes.brand = formData.brand;
    if (formData.category !== product.category) changes.category = formData.category;

    if (Object.keys(changes).length === 0) {
      onCancel();
      return;
    }

    try {
      await updateProduct.mutateAsync({
        productId: product.id,
        data: changes,
      });
      toast.success(t`Product updated`);
      onSuccess();
    } catch {
      toast.error(t`Failed to update product`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">
          <Trans>Name</Trans>
        </Label>
        <Input
          id="name"
          value={formData.name || ''}
          onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
          required
        />
      </div>

      <div>
        <Label htmlFor="barcode">
          <Trans>Barcode</Trans>
        </Label>
        <Input
          id="barcode"
          value={formData.barcode || ''}
          onChange={(e) => setFormData((f) => ({ ...f, barcode: e.target.value || null }))}
        />
      </div>

      <div>
        <Label htmlFor="brand">
          <Trans>Brand</Trans>
        </Label>
        <Input
          id="brand"
          value={formData.brand || ''}
          onChange={(e) => setFormData((f) => ({ ...f, brand: e.target.value || null }))}
        />
      </div>

      <div>
        <Label htmlFor="category">
          <Trans>Category</Trans>
        </Label>
        <select
          id="category"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          value={formData.category || 'none'}
          onChange={(e) =>
            setFormData((f) => ({
              ...f,
              category:
                e.target.value === 'none'
                  ? null
                  : (e.target.value as AdminUpdateProductData['category']),
            }))
          }
        >
          <option value="none">{t`No category`}</option>
          {PRODUCT_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {getCategoryLabel(cat)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
          <XCircleIcon className="h-4 w-4 mr-2" />
          <Trans>Cancel</Trans>
        </Button>
        <Button type="submit" className="flex-1" disabled={updateProduct.isPending}>
          <CheckIcon className="h-4 w-4 mr-2" />
          {updateProduct.isPending ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
        </Button>
      </div>
    </form>
  );
}

interface DeleteProductDialogProps {
  product: AdminProduct;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteProductDialog({ product, onClose, onDeleted }: DeleteProductDialogProps) {
  const { t } = useLingui();
  const deleteProduct = useAdminDeleteProduct();

  const handleDelete = async () => {
    try {
      await deleteProduct.mutateAsync(product.id);
      toast.success(t`Product deleted`);
      onDeleted();
    } catch {
      toast.error(t`Failed to delete product`);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Delete Product</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Are you sure you want to delete <strong>{product.name}</strong>?
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">
                <Trans>This action cannot be undone.</Trans>
              </p>
              <p>
                {product.itemCount > 0 ? (
                  <Trans>
                    {product.itemCount} items are using this product. Their product reference will
                    be cleared.
                  </Trans>
                ) : (
                  <Trans>This product is not being used by any items.</Trans>
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteProduct.isPending}>
            {deleteProduct.isPending ? <Trans>Deleting...</Trans> : <Trans>Delete Product</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
